import { createContext, useContext, useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { useAuth } from "src/utils";

const SignalRContext = createContext(null);

export const SignalRProvider = ({ children, chats }) => {
  const { user } = useAuth();
  const connectionRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!user?.id) return;

    const baseUrl = import.meta.env.VITE_API_URL;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/chathub`)
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;

    const startConnection = async () => {
      try {
        await connection.start();
        console.log("SignalR connected");
        setConnected(true);

        // Join personal user group for new chat notifications
        await connection.invoke("JoinUser", user?.id);

        // Join all existing chat summary groups
        if (chats?.length) {
          const chatIds = chats.map(c => c.id);
          await connection.invoke("JoinUserChats", chatIds);
        }
      } catch (err) {
        console.error("SignalR connection error:", err);
      }
    };

    startConnection();

    connection.onreconnected(async () => {
      try {
        await connection.invoke("JoinUser", user.id);

        if (chats?.length) {
          const chatIds = chats.map(c => c.id);
          await connection.invoke("JoinUserChats", chatIds);
        }
      } catch (err) {
        console.error("SignalR reconnection error:", err);
      }
    });

    return () => {
      if (chats?.length) {
        const chatIds = chats.map(c => c.id);
        connection.invoke("LeaveUserChats", chatIds).catch(() => {});
      }
      // Optionally leave personal user group
      connection.invoke("LeaveUser", user.id).catch(() => {});
      connection.stop().catch(() => {});
    };
  }, [user?.id, chats]);

  return (
    <SignalRContext.Provider value={{ connection: connectionRef.current, connected }}>
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalR = () => useContext(SignalRContext);
